import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Building2, Users2, KeyRound, History, Settings, HeartPulse, ShieldCheck, Settings2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { cn } from "@/lib/utils";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const ADMIN_LINKS = [
  { to: "/erp/governance/users", label: "Users", icon: Users2 },
  { to: "/erp/governance/access", label: "Access", icon: KeyRound },
  { to: "/erp/governance/kpi-settings", label: "Alert thresholds", icon: History },
  { to: "/erp/governance/audit-log", label: "Audit Log", icon: History },
  { to: "/erp/governance/org-settings", label: "Organization Settings", icon: Settings },
  { to: "/erp/governance/system-health", label: "Backup & Health", icon: HeartPulse },
  { to: "/erp/governance/integrity-checks", label: "Integrity Checks", icon: ShieldCheck },
];

/** Bottom of the sidebar: company and administration. (Search, bell, user and log out are in the top bar.) */
export function SideUserPanel({ collapsed }) {
  const location = useLocation();
  const { isOrgAdmin } = useAuth();          // every page in this menu is Admin-only on the server
  const { companies, selectedCompanyId, setSelectedCompanyId } = useCompany() || {};
  const [adminOpen, setAdminOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const adminRef = useRef(null);

  useEffect(() => { setAdminOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!adminOpen) return undefined;
    const h = (e) => {
      if (adminRef.current?.contains(e.target) || document.querySelector("[data-admin-portal]")?.contains(e.target)) return;
      setAdminOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [adminOpen]);

  const toggleAdmin = () => {
    const r = adminRef.current.getBoundingClientRect();
    const h = ADMIN_LINKS.length * 42 + 48;
    setPos(collapsed ? { left: r.right + 8, top: Math.max(8, Math.min(r.top, window.innerHeight - h - 16)) } : { left: r.left, top: Math.max(8, r.top - h - 8) });
    setAdminOpen((v) => !v);
  };

  const adminMenu = adminOpen && createPortal(
    <div data-admin-portal style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }} className="min-w-[210px] overflow-hidden rounded-xl border border-white/10 bg-[#1F2B47] py-1 shadow-2xl">
      <p className="px-4 pb-1.5 pt-2.5 text-[10px] font-bold uppercase tracking-widest text-[#6B7A8D]">Administration</p>
      {ADMIN_LINKS.map((l) => (
        <NavLink key={l.to} to={l.to} onClick={() => setAdminOpen(false)} data-testid={`admin-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => cn("flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium transition-colors", isActive ? "bg-[#99002B] text-white" : "text-[#E6EBF5]/85 hover:bg-white/10 hover:text-white")}>
          <l.icon className="h-4 w-4 shrink-0" /> {l.label}
        </NavLink>
      ))}
    </div>,
    document.body
  );

  if (collapsed) {
    return (
      <div className="relative z-10 flex shrink-0 flex-col items-center gap-2 border-t border-white/10 px-2 py-3">
        {adminMenu}
        {isOrgAdmin && (
          <button ref={adminRef} onClick={toggleAdmin} title="Administration" data-testid="admin-toggle-button"
            className={cn("flex h-10 w-10 items-center justify-center rounded-lg transition-colors", adminOpen ? "bg-[#99002B] text-white" : "bg-[#24324E] text-[#E6EBF5] hover:bg-[#2B3A59]")}>
            <Settings2 className="h-5 w-5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative z-10 shrink-0 space-y-2.5 border-t border-white/10 px-2.5 py-3">
      {adminMenu}
      {companies && companies.length > 1 && (
        <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
          <SelectTrigger data-testid="company-selector" className="h-10 w-full gap-2 rounded-[10px] border-white/10 bg-white/[0.07] px-3 text-sm text-[#ECF4F7] hover:bg-white/10 focus:ring-[#99002B]">
            <Building2 className="h-3.5 w-3.5 shrink-0 text-[#8B9BB8]" />
            <span className="truncate"><SelectValue placeholder="Select company" /></span>
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.code}`}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {isOrgAdmin && (
        <button ref={adminRef} onClick={toggleAdmin} data-testid="admin-toggle-button"
          className={cn("flex w-full items-center gap-3 rounded-[10px] px-3.5 py-3 text-sm font-semibold transition-all", adminOpen ? "bg-[#99002B] text-white" : "bg-[#24324E] text-[#ECF4F7] hover:bg-[#2B3A59]")}>
          <Settings2 className="h-5 w-5 shrink-0" />
          <span className="flex-1 truncate text-left">Administration</span>
          <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform", adminOpen && "rotate-180")} />
        </button>
      )}
    </div>
  );
}
